import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from '../components/ui/dialog';
import { toast } from 'sonner';
import TaskCompletionCelebration from '../components/TaskCompletionCelebration';
import { 
  ArrowLeft, 
  CheckSquare, 
  Sun, 
  Moon, 
  Sparkles, 
  Send, 
  Clock, 
  User,
  UserPlus,
  MessageSquare,
  RefreshCw,
  Calendar as CalendarIcon,
  Paperclip,
  FileText,
  Image as ImageIcon,
  File,
  Download,
  Trash2,
  Upload,
  Pencil,
  Check,
  X,
  ImagePlus,
  ListTodo,
  ExternalLink,
  Mail,
  Plus,
  ArrowUpDown,
  RotateCcw,
  Reply
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import SubTasks from '../components/SubTasks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to convert URLs to clickable links
const LinkifyText = ({ text, isOwnMessage = false }) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  
  return (
    <>
      {parts.map((part, index) => {
        if (part.match(urlRegex)) {
          return (
            <a
              key={index}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className={`underline break-all font-medium ${
                isOwnMessage 
                  ? 'text-yellow-300 hover:text-yellow-200' 
                  : 'text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </>
  );
};

// Image Modal Component
const ImageModal = ({ isOpen, onClose, imageSrc, onDownload }) => {
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
        <DialogHeader className="p-4 border-b">
          <DialogTitle className="flex items-center justify-between">
            <span>Image Preview</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onDownload} data-testid="download-image-btn">
                <Download className="w-4 h-4 mr-2" />
                Save Image
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-center p-4 bg-black/5 dark:bg-white/5 overflow-auto max-h-[70vh]">
          <img 
            src={imageSrc} 
            alt="Full size preview" 
            className="max-w-full max-h-full object-contain"
            data-testid="modal-image"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TaskDetail = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [task, setTask] = useState(null);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [pastedImage, setPastedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [chatSummary, setChatSummary] = useState('');
  const [summarizing, setSummarizing] = useState(false);
  const [reprioritizing, setReprioritizing] = useState(false);
  const [addParticipantOpen, setAddParticipantOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [newDescription, setNewDescription] = useState('');
  const [editingDueDate, setEditingDueDate] = useState(false);
  const [newDueDate, setNewDueDate] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editMessageContent, setEditMessageContent] = useState('');
  const [replyTo, setReplyTo] = useState(null); // {message_id, sender_name, content}
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Celebration modal state
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationTaskName, setCelebrationTaskName] = useState('');
  // New state for Enhancement #1
  const [editingTaskName, setEditingTaskName] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [changingPrimaryAssignee, setChangingPrimaryAssignee] = useState(false);
  // State for subtask controls in header
  const [subtaskControls, setSubtaskControls] = useState({
    sortMode: 'manual',
    setSortMode: () => {},
    openAddModal: () => {},
    total: 0,
    completed: 0
  });
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const messageInputRef = useRef(null);

  const fetchTask = useCallback(async () => {
    try {
      const response = await fetch(`${API}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTask(data);
        setNewDescription(data.description || '');
      } else {
        toast.error('Task not found');
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching task:', error);
    }
  }, [taskId, token, navigate]);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await fetch(`${API}/tasks/${taskId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [taskId, token]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTask(), fetchMessages(), fetchUsers()]);
      setLoading(false);
    };
    loadData();

    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [fetchTask, fetchMessages, fetchUsers]);

  // Scroll to bottom only when user sends a message (handled in handleSendMessage)
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Handle paste event for images
  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            setPastedImage(event.target.result);
          };
          reader.readAsDataURL(file);
        }
        break;
      }
    }
  }, []);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !pastedImage) return;

    setSendingMessage(true);
    try {
      const response = await fetch(`${API}/tasks/${taskId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          content: newMessage || '',
          image_data: pastedImage || null,
          reply_to: replyTo || null
        })
      });

      if (response.ok) {
        const message = await response.json();
        setMessages(prev => [...prev, message]);
        setNewMessage('');
        setPastedImage(null);
        setReplyTo(null);
        // Scroll to bottom after user sends a message
        setTimeout(() => scrollToBottom(), 100);
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleEditMessage = async (messageId) => {
    if (!editMessageContent.trim()) return;

    try {
      const response = await fetch(`${API}/tasks/${taskId}/messages/${messageId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content: editMessageContent })
      });

      if (response.ok) {
        const updatedMessage = await response.json();
        setMessages(prev => prev.map(m => m.id === messageId ? updatedMessage : m));
        setEditingMessageId(null);
        setEditMessageContent('');
        toast.success('Message updated');
      }
    } catch (error) {
      toast.error('Failed to edit message');
    }
  };

  const startEditingMessage = (message) => {
    setEditingMessageId(message.id);
    setEditMessageContent(message.content);
  };

  const cancelEditingMessage = () => {
    setEditingMessageId(null);
    setEditMessageContent('');
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setTask(updatedTask);
        
        // Show celebration modal when task is completed
        if (newStatus === 'completed') {
          setCelebrationTaskName(updatedTask.name || task?.name || 'Task');
          setShowCelebration(true);
        } else {
          toast.success(`Status updated to ${newStatus}`);
        }
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateDescription = async () => {
    try {
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ description: newDescription })
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setTask(updatedTask);
        setEditingDescription(false);
        toast.success('Description updated');
      }
    } catch (error) {
      toast.error('Failed to update description');
    }
  };

  const handleUpdateDueDate = async () => {
    if (!newDueDate) return;
    
    try {
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ due_date: format(newDueDate, 'yyyy-MM-dd') })
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setTask(updatedTask);
        setEditingDueDate(false);
        toast.success('Due date updated');
      }
    } catch (error) {
      toast.error('Failed to update due date');
    }
  };

  const handleGetSummary = async () => {
    setSummarizing(true);
    try {
      const response = await fetch(`${API}/tasks/${taskId}/chat-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setChatSummary(data.summary);
      }
    } catch (error) {
      toast.error('Failed to generate summary');
    } finally {
      setSummarizing(false);
    }
  };

  const handleReprioritize = async () => {
    setReprioritizing(true);
    try {
      const response = await fetch(`${API}/tasks/${taskId}/reprioritize`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTask(prev => ({ ...prev, ai_priority: data.priority, ai_priority_reason: data.reason }));
        toast.success(`Priority updated to ${data.priority}`);
      }
    } catch (error) {
      toast.error('Failed to reprioritize');
    } finally {
      setReprioritizing(false);
    }
  };

  const handleAddParticipant = async (userId) => {
    try {
      const response = await fetch(`${API}/tasks/${taskId}/participants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: userId })
      });

      if (response.ok) {
        await fetchTask();
        setAddParticipantOpen(false);
        toast.success('Participant added');
      }
    } catch (error) {
      toast.error('Failed to add participant');
    }
  };

  // Enhancement #1: Update Task Name
  const handleUpdateTaskName = async () => {
    if (!newTaskName.trim()) {
      toast.error('Task name cannot be empty');
      return;
    }
    try {
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ name: newTaskName.trim() })
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setTask(updatedTask);
        setEditingTaskName(false);
        toast.success('Task name updated');
      } else {
        toast.error('Failed to update task name');
      }
    } catch (error) {
      toast.error('Failed to update task name');
    }
  };

  // Enhancement #1: Change Primary Assignee
  const handleChangePrimaryAssignee = async (newAssigneeId) => {
    try {
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ assigned_to: newAssigneeId })
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setTask(updatedTask);
        setChangingPrimaryAssignee(false);
        toast.success('Primary assignee updated');
      } else {
        toast.error('Failed to update primary assignee');
      }
    } catch (error) {
      toast.error('Failed to update primary assignee');
    }
  };

  // Enhancement #1: Remove Secondary Participant
  const handleRemoveSecondaryParticipant = async (participantId) => {
    try {
      const response = await fetch(`${API}/tasks/${taskId}/participants/${participantId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchTask();
        toast.success('Participant removed');
      } else {
        toast.error('Failed to remove participant');
      }
    } catch (error) {
      toast.error('Failed to remove participant');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) {
      toast.error('File size exceeds 100MB limit');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/tasks/${taskId}/attachments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        await fetchTask();
        toast.success('File uploaded successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload file');
      }
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await fetch(
        `${API}/tasks/${taskId}/attachments/${attachment.id}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Use original_filename if available, fallback to filename
        a.download = attachment.original_filename || attachment.filename || 'download';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Download started');
      } else {
        toast.error('Failed to download file');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      const response = await fetch(
        `${API}/tasks/${taskId}/attachments/${attachmentId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        await fetchTask();
        toast.success('Attachment deleted');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete attachment');
      }
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const openImageModal = (imageSrc) => {
    setSelectedImage(imageSrc);
    setImageModalOpen(true);
  };

  const handleDownloadImage = () => {
    if (selectedImage) {
      const a = document.createElement('a');
      a.href = selectedImage;
      a.download = `chat-image-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const handleDeleteTask = async () => {
    if (!window.confirm('Are you sure you want to delete this task? This action will move it to the deleted tasks list.')) {
      return;
    }

    setDeleting(true);
    try {
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Task deleted successfully');
        navigate('/');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete task');
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    } finally {
      setDeleting(false);
    }
  };

  // Check if current user can delete the task
  const canDeleteTask = () => {
    if (!task || !user) return false;
    const isOwner = task.assigned_by === user.id;
    const isSuperUser = user.role === 'super_user';
    return isOwner || isSuperUser;
  };

  const isTaskDeleted = task?.deleted === true;
  const isSuperUser = user?.role === 'super_user';

  const handleRestoreTask = async () => {
    try {
      const response = await fetch(`${API}/admin/restore-tasks`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_ids: [taskId] })
      });
      if (response.ok) {
        toast.success('Task restored successfully');
        fetchTask();
      } else {
        toast.error('Failed to restore task');
      }
    } catch (error) {
      toast.error('Failed to restore task');
    }
  };

  const handlePurgeTask = async () => {
    if (!window.confirm('This will PERMANENTLY delete this task and all its data. This cannot be undone. Continue?')) return;
    try {
      const response = await fetch(`${API}/admin/purge-tasks`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_ids: [taskId] })
      });
      if (response.ok) {
        toast.success('Task permanently deleted');
        navigate('/team-activity');
      } else {
        toast.error('Failed to permanently delete task');
      }
    } catch (error) {
      toast.error('Failed to permanently delete task');
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return <ImageIcon className="w-4 h-4" />;
    if (contentType?.includes('pdf')) return <FileText className="w-4 h-4" />;
    return <File className="w-4 h-4" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-purple-500/20 text-purple-600 dark:text-purple-400',
      acknowledged: 'status-acknowledged',
      'in progress': 'status-in-progress',
      completed: 'status-completed'
    };
    return styles[status] || '';
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'priority-high',
      medium: 'priority-medium',
      low: 'priority-low'
    };
    return styles[priority] || '';
  };

  const availableParticipants = users.filter(
    u => !task?.participants?.includes(u.id)
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading task...</div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">Task not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Image Modal */}
      <ImageModal 
        isOpen={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        imageSrc={selectedImage}
        onDownload={handleDownloadImage}
      />

      {/* Header */}
      <header className="sticky top-0 z-20 glass">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <img 
              src="/logo-dark.png" 
              alt="2BeLive" 
              className="h-10 w-auto hidden dark:block"
            />
            <img 
              src="/logo-light.png" 
              alt="2BeLive" 
              className="h-10 w-auto dark:hidden"
            />
            <h1 className="font-heading text-xl font-bold hidden sm:block">TaskFlow AI</h1>
          </div>

          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-btn">
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      {/* Main Content - 3 Column Layout: Task Info (1/4) | Sub-tasks (1/2) | Chat (1/4) */}
      <main className="max-w-[1800px] mx-auto px-4 md:px-8 py-8 overflow-x-hidden">
        {/* Deleted Task Banner */}
        {isTaskDeleted && isSuperUser && (
          <div className="mb-6 p-4 rounded-lg border border-red-500/30 bg-red-500/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="deleted-task-banner">
            <div className="flex items-center gap-3">
              <Trash2 className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-500">This task has been deleted</p>
                <p className="text-sm text-muted-foreground">
                  Deleted by {task.deleted_by_name || 'Unknown'} {task.deleted_at ? `on ${new Date(task.deleted_at).toLocaleDateString()}` : ''}. This is a read-only view.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button size="sm" variant="outline" onClick={handleRestoreTask} data-testid="restore-task-btn">
                <RotateCcw className="w-4 h-4 mr-2" />
                Restore
              </Button>
              <Button size="sm" variant="destructive" onClick={handlePurgeTask} data-testid="purge-task-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Permanently Delete
              </Button>
            </div>
          </div>
        )}
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Column 1: Task Details (1/4) */}
          <div className="lg:col-span-1 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                {/* Editable Task Name */}
                {editingTaskName ? (
                  <div className="space-y-2">
                    <Input
                      value={newTaskName}
                      onChange={(e) => setNewTaskName(e.target.value)}
                      placeholder="Task name..."
                      className="font-heading text-lg"
                      data-testid="task-name-input"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleUpdateTaskName} data-testid="save-task-name-btn">
                        <Check className="w-4 h-4 mr-1" />
                        Save
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => {
                          setEditingTaskName(false);
                          setNewTaskName(task.name);
                        }}
                        data-testid="cancel-task-name-btn"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap flex-1">
                      <CardTitle className="font-heading text-lg">{task.name}</CardTitle>
                      {task.external_submitter && (
                        <Badge variant="secondary" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Intake
                        </Badge>
                      )}
                    </div>
                    {!isTaskDeleted && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onClick={() => {
                        setNewTaskName(task.name);
                        setEditingTaskName(true);
                      }}
                      data-testid="edit-task-name-btn"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    )}
                  </div>
                )}
                {task.external_submitter && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-blue-400">
                    <Mail className="w-4 h-4" />
                    <span>Submitted by: <span className="font-medium">{task.external_submitter.email}</span></span>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4 pt-0">
                {/* Editable Description */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-muted-foreground">Description</label>
                    {!editingDescription && !isTaskDeleted && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2"
                        onClick={() => setEditingDescription(true)}
                        data-testid="edit-description-btn"
                      >
                        <Pencil className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                    )}
                  </div>
                  {editingDescription ? (
                    <div className="space-y-2">
                      <Textarea
                        value={newDescription}
                        onChange={(e) => setNewDescription(e.target.value)}
                        placeholder="Add a description..."
                        rows={3}
                        data-testid="description-textarea"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleUpdateDescription} data-testid="save-description-btn">
                          <Check className="w-4 h-4 mr-1" />
                          Save
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => {
                            setEditingDescription(false);
                            setNewDescription(task.description || '');
                          }}
                          data-testid="cancel-description-btn"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-1 text-sm whitespace-pre-wrap">
                      {task.description ? <LinkifyText text={task.description} /> : <span className="text-muted-foreground italic">No description</span>}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-violet-500" />
                      AI Priority
                    </label>
                    <div className="mt-1 flex items-center gap-2">
                      <Badge variant="outline" className={getPriorityBadge(task.ai_priority)}>
                        {task.ai_priority || 'N/A'}
                      </Badge>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={handleReprioritize}
                        disabled={reprioritizing}
                        data-testid="reprioritize-btn"
                      >
                        <RefreshCw className={`w-4 h-4 ${reprioritizing ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  </div>
                </div>

                {task.ai_priority_reason && (
                  <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
                    <div className="flex items-center gap-2 text-sm font-medium text-violet-600 dark:text-violet-400 mb-1">
                      <Sparkles className="w-4 h-4" />
                      AI Analysis
                    </div>
                    <p className="text-sm text-muted-foreground">{task.ai_priority_reason}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {/* Editable Due Date */}
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" />
                        Due Date
                      </label>
                      {!isTaskDeleted && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2"
                        onClick={() => {
                          setNewDueDate(parseISO(task.due_date));
                          setEditingDueDate(true);
                        }}
                        data-testid="edit-due-date-btn"
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      )}
                    </div>
                    <p className="mt-1 text-sm">{format(parseISO(task.due_date), 'PPP')}</p>
                    
                    <Dialog open={editingDueDate} onOpenChange={setEditingDueDate}>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Change Due Date</DialogTitle>
                        </DialogHeader>
                        <div className="flex justify-center">
                          <Calendar
                            mode="single"
                            selected={newDueDate}
                            onSelect={setNewDueDate}
                            initialFocus
                          />
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setEditingDueDate(false)}>Cancel</Button>
                          <Button onClick={handleUpdateDueDate} data-testid="save-due-date-btn">Save</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Assigned
                    </label>
                    <p className="mt-1 text-sm">{format(parseISO(task.date_assigned), 'PPP')}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Assigned By</label>
                    <div className="mt-1 flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                          {task.assigned_by_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{task.assigned_by_name}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Assigned To</label>
                    <div className="mt-1 flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs bg-blue-500 text-white">
                          {task.assigned_to_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{task.assigned_to_name}</span>
                    </div>
                  </div>
                </div>

                {/* Status - Editable for owner/assignee, read-only for others */}
                <div className={`p-3 rounded-lg border-l-4 ${
                  task.status === 'completed' 
                    ? 'bg-green-500/10 border-green-500' 
                    : task.status === 'in progress' 
                      ? 'bg-orange-500/10 border-orange-500' 
                      : 'bg-blue-500/10 border-blue-500'
                }`}>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  
                  {/* Editable dropdown - only for owner or primary assignee, disabled for deleted */}
                  {!isTaskDeleted && (task.assigned_by === user?.id || task.assigned_to === user?.id) ? (
                    <Select value={task.status} onValueChange={handleStatusChange}>
                      <SelectTrigger 
                        className={`mt-1 h-10 font-medium ${
                          task.status === 'completed'
                            ? 'text-green-600 dark:text-green-400'
                            : task.status === 'in progress'
                              ? 'text-orange-600 dark:text-orange-400'
                              : 'text-blue-600 dark:text-blue-400'
                        }`}
                        data-testid="status-select"
                      >
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${
                            task.status === 'completed'
                              ? 'bg-green-500'
                              : task.status === 'in progress'
                                ? 'bg-orange-500'
                                : 'bg-blue-500'
                          }`} />
                          <SelectValue />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500" />
                            New
                          </div>
                        </SelectItem>
                        <SelectItem value="in progress">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-orange-500" />
                            In Progress
                          </div>
                        </SelectItem>
                        <SelectItem value="completed">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                            Completed
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    /* Read-only status badge for other participants */
                    <div className={`mt-1 h-10 flex items-center gap-2 px-3 rounded-md border bg-muted/50 ${
                      task.status === 'completed'
                        ? 'text-green-600 dark:text-green-400'
                        : task.status === 'in progress'
                          ? 'text-orange-600 dark:text-orange-400'
                          : 'text-blue-600 dark:text-blue-400'
                    }`}>
                      <span className={`w-2 h-2 rounded-full ${
                        task.status === 'completed'
                          ? 'bg-green-500'
                          : task.status === 'in progress'
                            ? 'bg-orange-500'
                            : 'bg-blue-500'
                      }`} />
                      <span className="font-medium capitalize">{task.status}</span>
                    </div>
                  )}
                </div>

                {/* Delete Task Button - Only visible to owner or super admin, hidden for deleted */}
                {canDeleteTask() && !isTaskDeleted && (
                  <div className="pt-3 border-t mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                      onClick={handleDeleteTask}
                      disabled={deleting}
                      data-testid="delete-task-btn"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                      {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Participants */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="font-heading text-base">Participants</CardTitle>
                {!isTaskDeleted && (
                <Dialog open={addParticipantOpen} onOpenChange={setAddParticipantOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7" data-testid="add-participant-btn">
                      <UserPlus className="w-3.5 h-3.5 mr-1" />
                      Add
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Secondary Participant</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground mb-4">
                      Secondary participants can view and collaborate on this task
                    </p>
                    <div className="space-y-2">
                      {availableParticipants.length === 0 ? (
                        <p className="text-muted-foreground text-sm">All users are already participants</p>
                      ) : (
                        availableParticipants.map(u => (
                          <Button
                            key={u.id}
                            variant="outline"
                            className="w-full justify-start"
                            onClick={() => handleAddParticipant(u.id)}
                            data-testid={`add-participant-${u.id}`}
                          >
                            <Avatar className="h-6 w-6 mr-2">
                              <AvatarFallback className="text-xs">
                                {u.name.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            {u.name}
                          </Button>
                        ))
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {/* Owner */}
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-green-500/10 border border-green-500/20">
                    <Avatar className="h-5 w-5">
                      <AvatarFallback className="text-[10px] bg-green-500 text-white">
                        {task.assigned_by_name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm flex-1 truncate">{task.assigned_by_name}</span>
                    <Badge variant="outline" className="bg-green-500/20 text-green-600 dark:text-green-400 text-[10px] px-1.5">
                      Owner
                    </Badge>
                  </div>
                  
                  {/* Primary Assignee - always show with edit option for owner */}
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <Avatar className="h-5 w-5">
                      <AvatarFallback className="text-[10px] bg-blue-500 text-white">
                        {task.assigned_to_name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm flex-1 truncate">{task.assigned_to_name}</span>
                    <Badge variant="outline" className="bg-blue-500/20 text-blue-600 dark:text-blue-400 text-[10px] px-1.5">
                      {task.assigned_to === task.assigned_by ? 'Owner & Assignee' : 'Assignee'}
                    </Badge>
                    {/* Change Primary Button - visible for task owner */}
                    {task.assigned_by === user?.id && (
                      <Dialog open={changingPrimaryAssignee} onOpenChange={setChangingPrimaryAssignee}>
                        <DialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6"
                            data-testid="change-primary-btn"
                            title="Reassign task"
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Reassign Task</DialogTitle>
                          </DialogHeader>
                          <p className="text-sm text-muted-foreground mb-4">
                            Select a new assignee for this task
                          </p>
                          <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {users
                              .filter(u => u.id !== task.assigned_to)
                              .map(u => (
                                <Button
                                  key={u.id}
                                  variant="outline"
                                  className="w-full justify-start"
                                  onClick={() => handleChangePrimaryAssignee(u.id)}
                                  data-testid={`select-primary-${u.id}`}
                                >
                                  <Avatar className="h-6 w-6 mr-2">
                                    <AvatarFallback className="text-xs">
                                      {u.name.charAt(0)}
                                    </AvatarFallback>
                                  </Avatar>
                                  {u.name}
                                </Button>
                              ))
                            }
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                  
                  {/* Secondary Participants */}
                  {task.secondary_participants?.length > 0 && (
                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground mb-2">Secondary Participants</p>
                      <div className="space-y-1.5">
                        {task.secondary_participants.map(pId => {
                          const participant = users.find(u => u.id === pId);
                          return (
                            <div key={pId} className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                              <Avatar className="h-5 w-5">
                                <AvatarFallback className="text-[10px]">
                                  {participant?.name?.charAt(0) || '?'}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-sm flex-1 truncate">{participant?.name || 'Unknown'}</span>
                              {/* Remove Secondary Participant Button */}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => handleRemoveSecondaryParticipant(pId)}
                                data-testid={`remove-participant-${pId}`}
                              >
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Column 2: Sub-Tasks (1/2 width) */}
          <div className="lg:col-span-2 space-y-4">
            {/* Sub-Tasks - Now prominent */}
            <Card className="h-[calc(100vh-16rem)] flex flex-col">
              <CardHeader className="flex flex-row items-center justify-between pb-2 shrink-0 border-b">
                <div className="flex items-center gap-2">
                  <CardTitle className="font-heading text-base flex items-center gap-2">
                    <ListTodo className="w-5 h-5" />
                    Sub-Tasks
                  </CardTitle>
                  {subtaskControls.total > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {subtaskControls.completed}/{subtaskControls.total}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Select 
                    value={subtaskControls.sortMode} 
                    onValueChange={subtaskControls.setSortMode}
                  >
                    <SelectTrigger className="w-[130px] h-8">
                      <ArrowUpDown className="w-3 h-3 mr-1" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual Order</SelectItem>
                      <SelectItem value="due_date">Due Date</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    size="sm" 
                    className="h-8"
                    onClick={subtaskControls.openAddModal} 
                    data-testid="add-subtask-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden pt-3">
                <ScrollArea className="h-full">
                  <SubTasks 
                    taskId={taskId} 
                    participants={task?.participants || []}
                    externalControls={true}
                    onControlsReady={setSubtaskControls}
                  />
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Attachments */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <Paperclip className="w-5 h-5" />
                  Attachments
                </CardTitle>
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="h-7"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || isTaskDeleted}
                    data-testid="upload-file-btn"
                  >
                    <Upload className={`w-3.5 h-3.5 mr-1 ${uploading ? 'animate-pulse' : ''}`} />
                    {uploading ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {task.attachments?.length === 0 ? (
                  <div className="text-center text-muted-foreground py-4">
                    <Paperclip className="w-8 h-8 mx-auto mb-2 opacity-20" />
                    <p className="text-sm">No attachments yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {task.attachments?.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between p-2 rounded-lg bg-muted"
                        data-testid={`attachment-${attachment.id}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {getFileIcon(attachment.content_type)}
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{attachment.original_filename}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatFileSize(attachment.file_size)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleDownloadAttachment(attachment)}
                            data-testid={`download-${attachment.id}`}
                          >
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          {(attachment.uploaded_by === user?.id || task.assigned_by === user?.id) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive hover:text-destructive"
                              onClick={() => handleDeleteAttachment(attachment.id)}
                              data-testid={`delete-${attachment.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Column 3: Chat Section (1/4 width) */}
          <div className="lg:col-span-1">
            <Card className="h-[calc(100vh-12rem)]">
              <CardHeader className="flex flex-row items-center justify-between border-b py-3 px-4">
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Chat
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="h-7 px-2"
                  onClick={handleGetSummary}
                  disabled={summarizing || messages.length === 0}
                  data-testid="summarize-chat-btn"
                >
                  <Sparkles className={`w-3.5 h-3.5 ${summarizing ? 'animate-pulse' : ''}`} />
                </Button>
              </CardHeader>
              <CardContent className="p-0 flex flex-col h-[calc(100%-4rem)]">
                {/* AI Summary */}
                {chatSummary && (
                  <div className="p-3 border-b bg-violet-500/5">
                    <div className="flex items-center gap-2 text-xs font-medium text-violet-600 dark:text-violet-400 mb-1">
                      <Sparkles className="w-3 h-3" />
                      AI Summary
                    </div>
                    <p className="text-xs text-muted-foreground">{chatSummary}</p>
                  </div>
                )}

                {/* Messages */}
                <ScrollArea className="flex-1 p-3">
                  {messages.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center text-muted-foreground">
                        <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-20" />
                        <p className="text-sm">No messages yet</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {messages.map((msg) => {
                        const isOwn = msg.sender_id === user?.id;
                        const isEditing = editingMessageId === msg.id;
                        
                        return (
                          <div
                            key={msg.id}
                            className={`group flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                            data-testid={`message-${msg.id}`}
                          >
                            <div className={`flex gap-1.5 ${isOwn ? 'flex-row-reverse' : ''} max-w-[95%]`}>
                              <Avatar className="h-6 w-6 flex-shrink-0">
                                <AvatarFallback className={`text-[10px] ${isOwn ? 'bg-blue-500 text-white' : ''}`}>
                                  {msg.sender_name?.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <div className={`chat-bubble p-2 rounded-lg ${isOwn ? 'chat-bubble-own' : 'chat-bubble-other'}`}>
                                <div className="flex items-center justify-between gap-1 mb-0.5">
                                  <p className="text-xs font-medium">{msg.sender_name}</p>
                                  <div className="flex items-center gap-0.5">
                                    {!isEditing && !isTaskDeleted && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-4 w-4 opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity"
                                        onClick={() => {
                                          setReplyTo({
                                            message_id: msg.id,
                                            sender_name: msg.sender_name,
                                            content: (msg.content || '').substring(0, 100)
                                          });
                                          messageInputRef.current?.focus();
                                        }}
                                        data-testid={`reply-message-${msg.id}`}
                                      >
                                        <Reply className="w-2.5 h-2.5" />
                                      </Button>
                                    )}
                                    {isOwn && !isEditing && !msg.image_data && !isTaskDeleted && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-4 w-4 opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity"
                                        onClick={() => startEditingMessage(msg)}
                                        data-testid={`edit-message-${msg.id}`}
                                      >
                                        <Pencil className="w-2.5 h-2.5" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Reply Quote Block */}
                                {msg.reply_to && (
                                  <button
                                    className={`w-full text-left mb-1.5 p-1.5 rounded border-l-2 cursor-pointer transition-colors ${
                                      isOwn 
                                        ? 'bg-white/10 border-white/40 hover:bg-white/20' 
                                        : 'bg-muted/50 border-primary/40 hover:bg-muted'
                                    }`}
                                    onClick={() => {
                                      const el = document.querySelector(`[data-testid="message-${msg.reply_to.message_id}"]`);
                                      if (el) {
                                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        el.classList.add('ring-2', 'ring-primary', 'ring-opacity-50');
                                        setTimeout(() => el.classList.remove('ring-2', 'ring-primary', 'ring-opacity-50'), 2000);
                                      }
                                    }}
                                    data-testid={`reply-quote-${msg.id}`}
                                  >
                                    <p className={`text-[10px] font-medium flex items-center gap-1 ${isOwn ? 'text-white/80' : 'text-primary'}`}>
                                      <Reply className="w-2.5 h-2.5" />
                                      {msg.reply_to.sender_name}
                                    </p>
                                    <p className={`text-[10px] truncate ${isOwn ? 'text-white/60' : 'text-muted-foreground'}`}>
                                      {msg.reply_to.content || 'Image'}
                                    </p>
                                  </button>
                                )}
                                
                                {isEditing ? (
                                  <div className="space-y-2">
                                    <Textarea
                                      value={editMessageContent}
                                      onChange={(e) => setEditMessageContent(e.target.value)}
                                      className="min-h-[50px] bg-background text-foreground text-xs"
                                      data-testid="edit-message-textarea"
                                    />
                                    <div className="flex gap-1">
                                      <Button 
                                        size="sm" 
                                        className="h-6 text-xs px-2"
                                        onClick={() => handleEditMessage(msg.id)}
                                        data-testid="save-message-btn"
                                      >
                                        <Check className="w-3 h-3 mr-1" />
                                        Save
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="outline" 
                                        className="h-6 text-xs px-2"
                                        onClick={cancelEditingMessage}
                                        data-testid="cancel-message-btn"
                                      >
                                        <X className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    {msg.content && (
                                      <p className="text-xs whitespace-pre-wrap break-words">
                                        <LinkifyText text={msg.content} isOwnMessage={isOwn} />
                                      </p>
                                    )}
                                    {msg.image_data && (
                                      <div className="mt-1.5">
                                        <img 
                                          src={msg.image_data}
                                          alt="Shared image"
                                          className="max-w-full max-h-[120px] rounded cursor-pointer hover:opacity-90 transition-opacity object-cover"
                                          onClick={() => openImageModal(msg.image_data)}
                                          data-testid={`chat-image-${msg.id}`}
                                        />
                                      </div>
                                    )}
                                  </>
                                )}
                                
                                <div className={`text-[10px] mt-0.5 ${isOwn ? 'text-white/70' : 'text-muted-foreground'}`}>
                                  {formatDistanceToNow(parseISO(msg.created_at), { addSuffix: true })}
                                  {msg.edited && <span className="italic ml-1">(edited)</span>}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>

                {/* Pasted Image Preview */}
                {pastedImage && (
                  <div className="px-3 py-2 border-t bg-muted/50">
                    <div className="flex items-center gap-2">
                      <img 
                        src={pastedImage} 
                        alt="Pasted preview" 
                        className="h-12 w-12 object-cover rounded border"
                        data-testid="pasted-image-preview"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium flex items-center gap-1">
                          <ImagePlus className="w-3 h-3" />
                          Ready to send
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => setPastedImage(null)}
                        data-testid="remove-pasted-image"
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Reply Preview */}
                {replyTo && (
                  <div className="px-3 py-2 border-t bg-muted/50" data-testid="reply-preview">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0 border-l-2 border-primary pl-2">
                        <p className="text-[10px] font-medium text-primary flex items-center gap-1">
                          <Reply className="w-3 h-3" />
                          Replying to {replyTo.sender_name}
                        </p>
                        <p className="text-[10px] text-muted-foreground truncate">{replyTo.content || 'Image'}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-6 w-6 flex-shrink-0"
                        onClick={() => setReplyTo(null)}
                        data-testid="cancel-reply"
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Message Input */}
                <div className="p-3 border-t">
                  {isTaskDeleted ? (
                    <div className="text-center text-muted-foreground py-2 text-sm">
                      Chat is disabled for deleted tasks
                    </div>
                  ) : (
                  <form onSubmit={handleSendMessage} className="flex gap-1.5">
                    <Input
                      ref={messageInputRef}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onPaste={handlePaste}
                      placeholder={pastedImage ? "Message..." : "Type a message..."}
                      disabled={sendingMessage}
                      className="text-sm h-8"
                      data-testid="message-input"
                    />
                    <Button 
                      type="submit" 
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      disabled={sendingMessage || (!newMessage.trim() && !pastedImage)} 
                      data-testid="send-message-btn"
                    >
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                  </form>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Task Completion Celebration Modal */}
      <TaskCompletionCelebration
        isOpen={showCelebration}
        onClose={() => setShowCelebration(false)}
        taskName={celebrationTaskName}
        token={token}
      />
    </div>
  );
};

export default TaskDetail;
